import { type Combobox } from '@base-ui/react/combobox';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import { flattenItemsForRender, getSearchExpandedValues } from '../utils/tree';
import { useDropdownExpansion } from './useDropdownExpansion';
import { useDropdownSelection } from './useDropdownSelection';
import { useDropdownTreeData } from './useDropdownTreeData';
import { useDropdownVisibility } from './useDropdownVisibility';
import type {
  DropdownValue,
  FilterItems,
  MatchesItem,
  FlatItem,
  InitialSelectionMode,
  NestedGroupItem,
} from '../types';

const EMPTY_DROPDOWN_VALUES: DropdownValue[] = [];

export type UseDropdownTreeResult = {
  allItems: FlatItem[];
  allValues: DropdownValue[];
  childrenByValue: Map<DropdownValue, DropdownValue[]>;
  descendantsByValue: Map<DropdownValue, DropdownValue[]>;
  labelByValue: Map<DropdownValue, string>;
  partialByValue: Map<DropdownValue, boolean>;
  expandedSet: Set<DropdownValue>;
  selectedSet: Set<DropdownValue>;
  selectedValues: DropdownValue[];
  visibleTree: NestedGroupItem[];
  visibleItems: FlatItem[];
  visibleValues: DropdownValue[];
  searchTerm: string;
  allSelected: boolean;
  partiallySelected: boolean;
  handleSelectionChange: (
    nextValues: DropdownValue[] | DropdownValue | null,
    event?: Combobox.Root.ChangeEventDetails,
  ) => DropdownValue[];
  setSearchTerm: Dispatch<SetStateAction<string>>;
  toggleSelectionForValue: (value: DropdownValue) => DropdownValue[];
  toggleAllSelections: () => DropdownValue[];
  toggleExpanded: (value: DropdownValue) => void;
};

type UseDropdownTreeOptions = {
  items: NestedGroupItem[];
  multiple: boolean;
  searchable: boolean;
  hideSearchParents?: boolean;
  expandable: boolean;
  defaultExpanded?: boolean;
  selectedValues?: DropdownValue[];
  initialSelectedValues?: DropdownValue[];
  initialSelectionMode?: InitialSelectionMode;
  filterItems?: FilterItems;
  matchesItem?: MatchesItem;
  onSelectionChange?: (
    nextSelectedValues: DropdownValue[],
    event?: Combobox.Root.ChangeEventDetails,
  ) => void;
};

export function useDropdownTree({
  items,
  multiple,
  searchable,
  hideSearchParents = false,
  expandable,
  defaultExpanded,
  selectedValues,
  initialSelectedValues,
  initialSelectionMode,
  filterItems,
  matchesItem,
  onSelectionChange,
}: UseDropdownTreeOptions): UseDropdownTreeResult {
  const [searchTerm, setSearchTerm] = useState('');

  // Derive all necessary data structures from the input items.
  const treeData = useDropdownTreeData(items);
  const defaultExpandedValues = useMemo(
    () => (defaultExpanded ? treeData.expandableValues : EMPTY_DROPDOWN_VALUES),
    [defaultExpanded, treeData.expandableValues],
  );
  const normalizedSearch = searchable ? searchTerm.trim() : '';
  const searchExpandedValues = useMemo(
    () =>
      filterItems && !matchesItem
        ? treeData.expandableValues
        : getSearchExpandedValues(items, normalizedSearch, matchesItem),
    [filterItems, items, matchesItem, normalizedSearch, treeData.expandableValues],
  );

  // Manage expansion state.
  const expansionState = useDropdownExpansion(expandable, defaultExpandedValues, {
    term: normalizedSearch,
    expandableValues: searchExpandedValues,
  });

  // Determine visible items based on search term and expansion state.
  const { visibleTree, childrenByValue } = useDropdownVisibility({
    items,
    searchTerm,
    searchable,
    hideSearchParents,
    expandable,
    expandedSet: expansionState.expandedSet,
    filterItems,
    matchesItem,
  });
  const visibleItems = useMemo(() => flattenItemsForRender(visibleTree), [visibleTree]);
  const visibleValues = useMemo(
    () =>
      visibleItems
        .filter((item) => treeData.selectableByValue.get(item.value))
        .map((item) => item.value),
    [treeData.selectableByValue, visibleItems],
  );

  // Manage selection state and logic.
  const selectionState = useDropdownSelection({
    multiple,
    allItems: treeData.allItems,
    allValues: treeData.allValues,
    itemsByDepthDesc: treeData.itemsByDepthDesc,
    selectableByValue: treeData.selectableByValue,
    descendantsByValue: treeData.descendantsByValue,
    selectedValues,
    initialSelectedValues,
    initialSelectionMode,
    onSelectionChange,
  });

  return {
    ...selectionState,
    ...expansionState,
    allItems: treeData.allItems,
    allValues: treeData.allValues,
    visibleItems,
    visibleTree,
    visibleValues,
    childrenByValue,
    descendantsByValue: treeData.descendantsByValue,
    labelByValue: treeData.labelByValue,
    searchTerm,
    setSearchTerm,
  };
}
